// src/services/tokenService.ts
import { api } from './api';
import secureLocalStorage from 'react-secure-storage';
import { jwtDecode } from 'jwt-decode';
import { STORAGE_KEYS } from '../constants';
import { expireSession } from './sessionService';

interface TokenPayload {
  exp: number;
  iat: number;
  payload: string;
}

interface RefreshResponse {
  message: string;
  data: {
    accesstoken: string;
    refreshtoken: string;
  };
  error: boolean;
}

class TokenService {
  private lastActiveTime: number = Date.now();
  private refreshPromise: Promise<void> | null = null;
  private refreshIntervalId: number | null = null;
  private readonly ACTIVITY_THRESHOLD = 60 * 60 * 1000; // 1 hour in milliseconds
  private readonly TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes before expiry

  constructor() {
    // Set up activity tracking
    this.setupActivityTracking();
  }

  private setupActivityTracking(): void {
    // Update last active time on user interactions
    const updateActivity = () => {
      this.lastActiveTime = Date.now();
    };

    // Track common user interactions
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keypress', updateActivity);
    window.addEventListener('click', updateActivity);
    window.addEventListener('scroll', updateActivity);
    window.addEventListener('touchstart', updateActivity);
  }

  public isUserActive(): boolean {
    return Date.now() - this.lastActiveTime < this.ACTIVITY_THRESHOLD;
  }

  private getTokenExpiry(token: string): number {
    try {
      const decoded = jwtDecode<TokenPayload>(token);
      return decoded.exp * 1000; // Convert to milliseconds
    } catch {
      return 0;
    }
  }

  private getTimeUntilExpiry(): number | null {
    const accessToken = secureLocalStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) as string | null;
    if (!accessToken) {
      return null;
    }

    return this.getTokenExpiry(accessToken) - Date.now();
  }

  public hasTokenExpired(): boolean {
    const timeUntilExpiry = this.getTimeUntilExpiry();
    return timeUntilExpiry !== null && timeUntilExpiry <= 0;
  }

  public shouldRefreshToken(): boolean {
    const timeUntilExpiry = this.getTimeUntilExpiry();
    if (timeUntilExpiry === null) return false;

    // Return true if token will expire soon, has not yet expired, and the user is active.
    return timeUntilExpiry > 0 && timeUntilExpiry < this.TOKEN_REFRESH_THRESHOLD && this.isUserActive();
  }

  public async refreshToken(): Promise<void> {
    // If there's already a refresh in progress, return that promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = secureLocalStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) as string;
    if (!refreshToken) {
      expireSession();
      return Promise.reject(new Error('No refresh token available'));
    }

    // Create a new promise for this refresh operation
    this.refreshPromise = new Promise<void>((resolve, reject) => {
      api.post<RefreshResponse>(
        '/auth/refreshtoken',
        {}, // Empty body
        {
          headers: {
            'Authorization': `Bearer ${refreshToken}`
          }
        }
      )
        .then(response => {
          const { accesstoken, refreshtoken } = response.data.data;
          
          // Update tokens in storage
          secureLocalStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accesstoken);
          secureLocalStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshtoken);
          
          resolve();
        })
        .catch(error => {
          reject(error);
        })
        .finally(() => {
          // Clear the promise so future calls can try again
          this.refreshPromise = null;
        });
    });

    return this.refreshPromise;
  }

  public syncTokenState(): void {
    const accessToken = secureLocalStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) as string | null;
    if (!accessToken) {
      return;
    }

    if (this.hasTokenExpired()) {
      if (this.isUserActive()) {
        this.refreshToken().catch(() => {
          // Refresh failed (e.g. refresh token also expired) — expire the session.
          // The response interceptor in api.ts will also call expireSession on a 401,
          // so this catch is a safety net for non-401 failures or timing races.
          expireSession();
        });
      } else {
        expireSession();
      }
      return;
    }

    if (this.shouldRefreshToken()) {
      this.refreshToken().catch(() => {
        // 401 refresh failures are handled centrally by the API interceptor.
      });
    }
  }

  public startTokenRefreshMonitor(): void {
    if (this.refreshIntervalId !== null) {
      return;
    }

    const checkInterval = 60 * 1000; // Check every minute

    this.refreshIntervalId = window.setInterval(() => {
      this.syncTokenState();
    }, checkInterval);
  }

  public stopTokenRefreshMonitor(): void {
    if (this.refreshIntervalId !== null) {
      window.clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }
  }
}

export const tokenService = new TokenService();
